import ko from 'knockout';
import moment from 'moment';

import empty from '@/util/empty';
import isObject from '@/util/isObject';
import unwrap from '@/util/unwrap';

ko.extenders.date = function (target, options): ObservableDate {
    const opts = {
        dateonly: false,
        ...options,
        default: unwrap(options, 'default') && moment(unwrap(options, 'default')),
    } satisfies DateOptions;

    let lastwrite: moment.Moment;
    const datetime = ko.pureComputed({
        read: target,
        write: function (newValue) {
            if (!empty(newValue)) {
                newValue = moment(newValue);
                newValue.dateonly = opts.dateonly;
                newValue = opts.dateonly ? newValue.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) : newValue;
            }
            if (newValue && lastwrite ? newValue.isValid() && !newValue.isSame(lastwrite) : newValue !== lastwrite) {
                lastwrite = moment(newValue);
                target(newValue);
            }
        },
    });
    const date = ko.pureComputed({
        read: () => datetime()?.asDateOnly(),
        write: val => {
            const timepart =
                unwrap(
                    [datetime(), opts.default].find(t => t?.isValid()),
                    'asTime',
                ) || '00:00:00';
            datetime(moment.fromDateOnly(val).asDateOnly() + ' ' + timepart);
        },
    });
    const time = ko.pureComputed({
        read: () => datetime()?.asTime(),
        write: val => {
            const datepart =
                unwrap(
                    [datetime(), opts.default].find(t => t?.isValid()),
                    'asDateOnly',
                ) || moment().asDateOnly();
            datetime(datepart + ' ' + moment.fromTime(val).asTime());
        },
    });

    datetime(target());
    return Object.assign(datetime, { date, time });
};
interface ObservableDate extends ko.PureComputed {
    date: ko.PureComputed<string>;
    time: ko.PureComputed<string>;
}
type DateOptions = {
    dateonly?: boolean;
    default?: moment.Moment;
};
declare module 'knockout' {
    export interface ExtendersOptions {
        date: DateOptions;
    }
}

// Options can be either an object or the precision.
ko.extenders.number = function (target, options2) {
    const options = {
        precision: undefined,
        default: undefined,
        ...(isObject(options2) ? options2 : { precision: options2 }),
    } satisfies NumberOptions;
    const roundingMultiplier = !empty(options.precision) && 10 ** options.precision;
    const result = ko.utils.beforeWrite(ko.observable<number>(), (newValue: unknown) => {
        let valueToWrite: number | undefined = Number.parseFloat(newValue as string);
        if (Number.isNaN(valueToWrite)) {
            // Invalid number, fallback to default.
            valueToWrite = options.default;
        } else if (roundingMultiplier) {
            valueToWrite = Math.round(valueToWrite * roundingMultiplier) / roundingMultiplier;
        }
        target(valueToWrite);
        return valueToWrite;
    });
    target.subscribeNow(result);
    return result;
};
type NumberOptions = {
    precision?: number;
    default?: number;
};
declare module 'knockout' {
    export interface ExtendersOptions {
        number: number | NumberOptions;
    }
}

// Return an observableArray that is kept in sync with the target observable.
// Useful when the value returned by objectTraverse(..., true) is not guaranteed to be observableArray.
ko.extenders.array = function (target) {
    if (ko.isObservableArray(target)) {
        return target;
    }
    const result = ko.observableArray(target());
    let updating = false;
    const update = (obs: ko.Subscribable<unknown[]>, val: unknown[]) => {
        if (!updating) {
            updating = true;
            obs(val);
            updating = false;
        }
    };
    target.subscribe((v: unknown[]) => update(result, v));
    result.subscribe(v => update(target, v));
    return result;
};
declare module 'knockout' {
    export interface ExtendersOptions {
        array: true;
    }
}

function compare_eqeqeq(a: unknown, b: unknown) {
    return a === b;
}
function compare_json(a: unknown, b: unknown) {
    return JSON.stringify(a) === JSON.stringify(b);
}

ko.extenders.equality = function (target, fn) {
    fn = fn === '===' ? compare_eqeqeq : fn === 'json' ? compare_json : fn;
    if (ko.isObservable(target) || ko.isComputed(target)) {
        target.equalityComparer = fn;
    } else {
        throw new Error('Cannot set equalityComparer - unexpected target type');
    }
    return target;
};
declare module 'knockout' {
    export interface ExtendersOptions {
        equality: '===' | 'json' | ((a: unknown, b: unknown) => boolean);
    }
}

// modifies a string so that it only consists of certain characters as specified by the parameter.
// example: idnumber.extend({ sanitize: '[0-9]' })
ko.extenders.sanitize = function (target, charset) {
    const pattern = new RegExp('(?!' + (charset instanceof RegExp ? charset.source : charset) + ').', 'g');
    const sanitize = (val: string) => (empty(val) ? val : val.replace(pattern, ''));
    target(sanitize(target()));
    return ko.utils.beforeWrite(target, sanitize);
};
declare module 'knockout' {
    export interface ExtendersOptions {
        sanitize: string | RegExp;
    }
}

// A shorthand method for rate-limiting a subscribable.
ko.extenders.debounce = function (target, timeout) {
    return target.extend({ rateLimit: { timeout, method: 'notifyWhenChangesStop' } });
};
declare module 'knockout' {
    export interface ExtendersOptions {
        debounce: number;
    }
}

ko.subscribable.fn.subscribeOnce = function (handler, owner, eventName) {
    const subscription = this.subscribe(
        function (newValue) {
            subscription.dispose();
            handler.call(this, newValue);
        },
        owner,
        eventName,
    );
    return subscription;
};
declare module 'knockout' {
    interface SubscribableFunctions<T> {
        subscribeOnce<TTarget = void>(
            callback: SubscriptionCallback<T, TTarget>,
            callbackTarget?: TTarget,
            event?: 'change',
        ): Subscription;
    }
}

ko.subscribable.fn.subscribeChanged = function <TTarget>(
    callback: SubscribeChangedCallback<unknown, TTarget>,
    callbackTarget: TTarget,
    event: string,
) {
    let prev = ko.utils.peekObservable(this);
    return this.subscribe(
        function (cur) {
            const _prev = prev;
            prev = cur;
            callback.call(this, cur, _prev);
        },
        callbackTarget,
        event,
    );
};
type SubscribeChangedCallback<T = unknown, TTarget = void> = (this: TTarget, newval: T, oldval: T) => void;
declare module 'knockout' {
    interface SubscribableFunctions<T> {
        subscribeChanged<TTarget = void>(
            callback: SubscribeChangedCallback<T, TTarget>,
            callbackTarget?: TTarget,
            event?: string,
        ): Subscription;
    }
}

ko.subscribable.fn.subscribeNow = function <TTarget>(
    callback: ko.SubscriptionCallback<unknown, TTarget>,
    callbackTarget: TTarget,
    event?: 'change',
) {
    callback.call(callbackTarget, ko.utils.peekObservable(this));
    return this.subscribe(callback, callbackTarget, event);
};
declare module 'knockout' {
    interface SubscribableFunctions<T> {
        subscribeNow<TTarget = void>(
            callback: SubscriptionCallback<T, TTarget>,
            callbackTarget?: TTarget,
            event?: 'change',
        ): Subscription;
    }
}
